import { useConditionalEffect } from 'react-delta';
import { useRecoilState, useRecoilValue } from 'recoil';
import cloneDeep from 'lodash/cloneDeep';

import * as K from 'common/constants';
import { QueryOrigin, SystemField } from 'common/graphqlTypes';
import {
  completeFilterWithoutTabState,
  queryTimeRangeState,
  teamEmailsState,
} from 'components/common/Managers/RouteStateManager.state';
import { savedSettingsState, settingsState } from 'components/common/Managers/SettingsManager.state';
import {
  countPerTabRequestState,
  isStandardViewState,
  resultsTabsWithFilterState,
  viewDisplaySettingsState,
} from 'components/common/Managers/ViewStateManager.state';
import { generateNewResultsTabsRequest } from 'hooks/useLRQ/useLRQ';
import { useDidResultsTabChange } from 'hooks/useDidResultsTabChange';
import { useDidViewChange } from 'hooks/useDidViewChange';
import { useIsNewSearch } from 'hooks/useIsNewSearch';
import { useResetScrollState } from 'hooks/useResetScrollState';

import LRQRequestManager from './LRQRequestManager';

// TODO: DPVZ-2188 - Add unit tests for this file

// divvy view-defined columns into what our settings expect - additionalFields and systemFields
const partitionFields = (columns?: string[]) => {
  const defaultObj = { additionalFields: new Array<string>(), systemFields: new Array<string>() };
  return (columns || []).reduce((acc, column) => {
    if (Object.values(SystemField).includes(column as SystemField)) {
      acc.systemFields.push(column);
    } else {
      acc.additionalFields.push(column);
    }
    return acc;
  }, defaultObj);
};

/**
 * This non-rendering component handles views. So far it updates settings when the view changes.
 */
export const ViewStateManager = () => {
  const viewDisplaySettings = useRecoilValue(viewDisplaySettingsState);
  const resultsTabsWithFilter = useRecoilValue(resultsTabsWithFilterState);
  const savedSettings = useRecoilValue(savedSettingsState);
  const [settings, setSettings] = useRecoilState(settingsState);
  const resetScrollState = useResetScrollState();
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const completeFilterWithoutTab = useRecoilValue(completeFilterWithoutTabState);
  const teamEmails = useRecoilValue(teamEmailsState);
  const [countPerTabRequest, setCountPerTabRequest] = useRecoilState(countPerTabRequestState);
  const isStandardView = useRecoilValue(isStandardViewState);
  const { didResultsTabChange } = useDidResultsTabChange();
  const { didViewChange, isNewView } = useDidViewChange();
  const isNewSearch = useIsNewSearch();

  // We know that `resultsTabsWithFilter` will always have `filter`, so we force this to be `string[]`, otherwise
  // it would be `(string | undefined)[]`. There might be a better way (like typing at the selector level).
  const tabFilters = resultsTabsWithFilter.map(({ filter }) => filter) as string[];

  // When switching to a view other than Standard, get settings from the view definition
  useConditionalEffect(() => {
    const newSettings = cloneDeep(settings);

    if (viewDisplaySettings[K.SETTING.DISPLAY_MODE]) {
      newSettings[K.SETTING.DISPLAY_MODE] = viewDisplaySettings[K.SETTING.DISPLAY_MODE];
    }
    if (viewDisplaySettings[K.SETTING.SORT_ORDER]) {
      newSettings[K.SETTING.SORT_ORDER] = viewDisplaySettings[K.SETTING.SORT_ORDER];
    }
    if (viewDisplaySettings[K.SETTING.FONT_FAMILY]) {
      newSettings[K.SETTING.FONT_FAMILY] = viewDisplaySettings[K.SETTING.FONT_FAMILY];
    }
    if (viewDisplaySettings?.columns?.length) {
      const { additionalFields, systemFields } = partitionFields(viewDisplaySettings?.columns);
      newSettings[K.SETTING.ADDITIONAL_FIELDS] = additionalFields;
      newSettings[K.SETTING.SYSTEM_FIELDS] = systemFields;
    }

    setSettings(newSettings);
    resetScrollState(settings.sortOrder !== newSettings.sortOrder);
  }, (isNewView || didResultsTabChange) && !isStandardView);

  // When switching from another view to Standard, restore settings from saved settings
  useConditionalEffect(() => {
    const newSettings = cloneDeep(settings);

    newSettings[K.SETTING.DISPLAY_MODE] = savedSettings[K.SETTING.DISPLAY_MODE];
    newSettings[K.SETTING.SORT_ORDER] = savedSettings[K.SETTING.SORT_ORDER];
    newSettings[K.SETTING.FONT_FAMILY] = savedSettings[K.SETTING.FONT_FAMILY];
    newSettings[K.SETTING.ADDITIONAL_FIELDS] = cloneDeep(savedSettings[K.SETTING.ADDITIONAL_FIELDS]);
    newSettings[K.SETTING.SYSTEM_FIELDS] = cloneDeep(savedSettings[K.SETTING.SYSTEM_FIELDS]);

    setSettings(newSettings);
    resetScrollState(settings.sortOrder !== newSettings.sortOrder);
  }, didViewChange && isStandardView);

  // Calculate search results per tab when a new search is performed and we are not just switching tabs
  useConditionalEffect(() => {
    const { startTimeNs, endTimeNs, requestTime } = queryTimeRange;

    setCountPerTabRequest(
      generateNewResultsTabsRequest({
        endTime: endTimeNs,
        filter: completeFilterWithoutTab,
        origin: QueryOrigin.SEARCH,
        requestTime,
        startTime: startTimeNs,
        tabFilters: [
          // Adding an empty filter to represent the result count when there is no tab filter
          // This is specially useful when the user lands on a tab with filters, so `activeSearchMatchCountEstimate`
          // is not useful
          '',
          ...tabFilters,
        ],
        teamEmails,
      }),
    );
  }, isNewSearch && !didResultsTabChange && tabFilters.length > 0);

  if (countPerTabRequest) {
    return (
      <>
        <LRQRequestManager request={countPerTabRequest} />
      </>
    );
  }

  return <></>;
};
